import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptorConfiguration,
    MsalService,
} from '@azure/msal-angular';
import { BrowserCacheLocation, CacheLookupPolicy, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { ContextStateService } from '@core/services/context-state.service';
import { environment } from '@environment';

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

function loggerCallback(logLevel: LogLevel, message: string) {
    const level = logLevel === LogLevel.Error ? 'error' : logLevel === LogLevel.Warning ? 'warn' : logLevel === LogLevel.Info ? 'info' : 'debug';
    const levelLabel = LogLevel[logLevel] ?? String(logLevel);
    const line = `[MSAL][${levelLabel}] ${message}`;
    if (level === 'error') {
        console.error(line);
    } else if (level === 'warn') {
        console.warn(line);
    } else if (level === 'info') {
        console.info(line);
    } else {
        console.debug(line);
    }
}

const MSALInstanceFactory = (): IPublicClientApplication =>
    new PublicClientApplication({
        auth: {
            clientId: environment.msalConfig.auth.clientId,
            authority: `https://${environment.msalConfig.auth.identityUrl}/${environment.msalConfig.auth.tenantId}/${environment.msalConfig.auth.signInPolicyName}`,
            redirectUri: '/',
            postLogoutRedirectUri: '/',
            knownAuthorities: [environment.msalConfig.auth.identityUrl],
            navigateToLoginRequestUrl: true,
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
            storeAuthStateInCookie: isIE,
        },
        system: {
            allowNativeBroker: false, // Disables WAM Broker
            loggerOptions: {
                loggerCallback,
                // Verbose MSAL logs for investigating silent vs popup / cache (temporary).
                logLevel: LogLevel.Error,
                piiLoggingEnabled: false,
            },
        },
    });

const MSALGuardConfigFactory = (contextStateService: ContextStateService): MsalGuardConfiguration => {
    const guardCfg: MsalGuardConfiguration = {
        interactionType: InteractionType.Redirect,
        authRequest: (_authService, _state) => ({
            scopes: [...environment.apiConfig.api.scopes],
            extraQueryParameters: { ui_locales: environment.translations.defaultLang.split('-')[0] },
            loginHint: contextStateService.currentUser()?.email,
        }),
    };

    return guardCfg;
};

function MSALInterceptorConfigFactory(contextStateService: ContextStateService): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, string[]>();
    environment.apiConfig.api.publicApiEndpoints.forEach(endpoint => protectedResourceMap.set(`${environment.apiConfig.api.uri}${endpoint}`, null));

    protectedResourceMap.set(environment.apiConfig.api.uri, environment.apiConfig.api.scopes);

    const config: MsalInterceptorConfiguration = {
        interactionType: InteractionType.Popup,
        protectedResourceMap,
        authRequest: (_msalService, _req, originalAuthRequest) => ({
            ...originalAuthRequest,
            cacheLookupPolicy: CacheLookupPolicy.Default,
            loginHint: contextStateService.currentUser()?.email,
        }),
    };

    return config;
}

export const AUTH_PROVIDERS = [
    {
        provide: MSAL_INSTANCE,
        useFactory: MSALInstanceFactory,
    },
    {
        provide: MSAL_GUARD_CONFIG,
        useFactory: MSALGuardConfigFactory,
        deps: [ContextStateService],
    },
    {
        provide: MSAL_INTERCEPTOR_CONFIG,
        useFactory: MSALInterceptorConfigFactory,
        deps: [ContextStateService],
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
];
